import React, { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import {
    Shield, ShieldAlert, Sparkles, Undo2, RefreshCw, Play, Loader2,
    FileText, Puzzle, ShoppingBag, MessageSquare, ToggleRight,
    AlertTriangle, Zap, Clock, CheckCircle2, BrainCircuit, ArrowUpRight,
    MailPlus, SearchCode,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";

const SEVERITY_COLOR = {
    low: "text-text-secondary bg-white/5 border-white/10",
    medium: "text-neutral-400 bg-neutral-400/10 border-neutral-400/30",
    high: "text-neutral-400 bg-neutral-400/10 border-neutral-400/30",
    critical: "text-[var(--color-alert)] bg-[var(--color-alert)]/10 border-[var(--color-alert)]/40",
};

function KpiCard({ icon: Icon, label, value, tone = "primary", testid }) {
    const toneClass = {
        primary: "text-[var(--color-primary)]",
        success: "text-[var(--color-success)]",
        alert: "text-[var(--color-alert)]",
        warning: "text-neutral-400",
        neutral: "text-text-secondary",
    }[tone];
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
                <Icon className={`h-3 w-3 ${toneClass}`} /> {label}
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${toneClass}`}>
                {value}
            </div>
        </div>
    );
}

function SectionShell({ title, subtitle, children, action }) {
    return (
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 md:p-6">
            <div className="flex items-start justify-between mb-4 gap-2">
                <div>
                    <h2 className="text-base md:text-lg font-semibold text-text-main">{title}</h2>
                    {subtitle && <p className="text-xs text-text-secondary mt-0.5">{subtitle}</p>}
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}

function RunNowButton({ label, onRun, testid }) {
    const [busy, setBusy] = useState(false);
    const run = async (dry_run) => {
        setBusy(true);
        try {
            const r = await onRun(dry_run);
            toast.success(`${label} ${dry_run ? "(dry-run)" : ""} done · ${r.drafts_generated ?? r.suggestions_generated ?? r.bundles_generated ?? r.alerts_found ?? 0} items`);
        } catch (e) { toast.error(e.message || `${label} failed`); }
        finally { setBusy(false); }
    };
    return (
        <div className="flex items-center gap-2">
            <button
                onClick={() => run(true)} disabled={busy}
                data-testid={`${testid}-dryrun`}
                className="text-[10px] uppercase tracking-widest rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5 disabled:opacity-50"
            >
                Dry run
            </button>
            <button
                onClick={() => run(false)} disabled={busy}
                data-testid={testid}
                className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-1.5 font-medium flex items-center gap-1 disabled:opacity-50"
            >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                Run now
            </button>
        </div>
    );
}

export default function AdminGovernance() {
    const { user: me } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rollingBack, setRollingBack] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.adminGovernanceDashboard();
            setData(r);
        } catch (e) {
            toast.error(e.message || "Failed to load governance dashboard");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const rollback = async (audit) => {
        const reason = prompt("Rollback reason (optional):") || "";
        setRollingBack(audit.id);
        try {
            const r = await api.adminGovernanceRollback(audit.id, reason);
            toast.success(`Rolled back · ${(r.reversed_actions || []).join(", ") || "no side effect"}`);
            await load();
        } catch (e) {
            toast.error(e.message || "Rollback failed");
        } finally {
            setRollingBack(null);
        }
    };

    const kpis = useMemo(() => {
        if (!data) return null;
        return {
            pending: data.autonomy_queue?.total_pending || 0,
            fraud_open: data.fraud_alerts?.open_count || 0,
            critical: data.fraud_alerts?.by_severity?.critical || 0,
            auto_reversible: (data.auto_actions?.recent || []).filter((r) => r.can_rollback).length,
            flags_on: (data.feature_flags || []).filter((f) => f.value === true).length,
        };
    }, [data]);

    if (!isAdminRole(me?.role)) {
        return (
            <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center">
                <h1 className="text-2xl">Admin only</h1>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full" data-testid="admin-governance">
                    <div className="flex items-center justify-between mb-6 gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                                Governance AI
                            </div>
                            <h1 className="text-3xl md:text-4xl font-semibold text-text-main">
                                Governance dashboard
                            </h1>
                            <p className="text-sm text-text-secondary mt-1">
                                Unified control over autonomy queue, fraud alerts, auto-actions and feature flags.
                            </p>
                        </div>
                        <button
                            onClick={load} data-testid="governance-refresh"
                            className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-2"
                        >
                            <RefreshCw className="h-3 w-3" /> Refresh
                        </button>
                    </div>

                    {loading || !data ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
                        </div>
                    ) : (
                        <>
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 mb-6">
                                <KpiCard icon={Sparkles} label="Autonomy pending" value={kpis.pending}
                                          tone="primary" testid="gov-kpi-pending" />
                                <KpiCard icon={ShieldAlert} label="Open fraud alerts" value={kpis.fraud_open}
                                          tone={kpis.critical > 0 ? "alert" : "warning"} testid="gov-kpi-fraud" />
                                <KpiCard icon={AlertTriangle} label="Critical fraud" value={kpis.critical}
                                          tone="alert" testid="gov-kpi-critical" />
                                <KpiCard icon={Undo2} label="Reversible actions" value={kpis.auto_reversible}
                                          tone="success" testid="gov-kpi-reversible" />
                                <KpiCard icon={ToggleRight} label="Feature flags ON" value={kpis.flags_on}
                                          tone="neutral" testid="gov-kpi-flags" />
                            </div>

                            <div className="grid gap-6 md:grid-cols-2 mb-6">
                                <SectionShell
                                    title="Autonomy queue"
                                    subtitle="Pending items awaiting human review across every AI surface."
                                >
                                    <ul className="space-y-2 text-sm">
                                        {[
                                            { k: "blog_drafts", label: "Blog drafts", icon: FileText },
                                            { k: "module_suggestions", label: "Module suggestions", icon: Puzzle },
                                            { k: "dynamic_offers", label: "Dynamic offers", icon: Sparkles },
                                            { k: "etf_bundle_drafts", label: "ETF bundle drafts", icon: ShoppingBag },
                                            { k: "faq_suggestions", label: "FAQ suggestions", icon: MessageSquare },
                                            { k: "content_optimization", label: "Content optimization (V30)", icon: Zap },
                                            { k: "content_growth_actions", label: "Growth actions (V30)", icon: Sparkles },
                                            { k: "content_recycling", label: "Recycling variants (V30)", icon: Puzzle },
                                            { k: "mini_landings", label: "Mini-landings (V30)", icon: FileText },
                                            { k: "v50_suggestions", label: "V50 self-opt suggestions", icon: BrainCircuit },
                                            { k: "email_suggestions", label: "Email Engine suggestions", icon: MailPlus },
                                            { k: "seo_suggestions", label: "SEO Engine suggestions", icon: SearchCode },
                                        ].map((row) => {
                                            const n = data.autonomy_queue?.[row.k] || 0;
                                            const Icon = row.icon;
                                            return (
                                                <li key={row.k}
                                                    data-testid={`gov-queue-${row.k}`}
                                                    className="flex items-center justify-between rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2"
                                                >
                                                    <span className="flex items-center gap-2 text-text-main">
                                                        <Icon className="h-4 w-4 text-[var(--color-primary)]" />
                                                        {row.label}
                                                    </span>
                                                    <span className="text-sm font-semibold tabular-nums">{n}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    <div className="mt-3 text-right">
                                        <a href="/admin/autonomy"
                                            data-testid="gov-open-autonomy"
                                            className="text-xs text-[var(--color-secondary)] hover:text-[var(--color-primary)]">
                                            Open autonomy queue →
                                        </a>
                                    </div>
                                </SectionShell>

                                <SectionShell
                                    title="Fraud engine"
                                    subtitle={data.fraud_alerts?.last_scan
                                        ? `Last scan · ${new Date(data.fraud_alerts.last_scan.started_at).toLocaleString()} · ${data.fraud_alerts.last_scan.alerts_found} alerts`
                                        : "No scan run yet."}
                                    action={<RunNowButton label="Fraud scan"
                                        testid="gov-fraud-run"
                                        onRun={async (dry) => {
                                            const r = await api.adminFraudScan(dry);
                                            await load();
                                            return r;
                                        }} />}
                                >
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {["critical", "high", "medium", "low"].map((sev) => (
                                            <span key={sev}
                                                data-testid={`gov-fraud-sev-${sev}`}
                                                className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${SEVERITY_COLOR[sev]}`}>
                                                {sev} · {data.fraud_alerts?.by_severity?.[sev] || 0}
                                            </span>
                                        ))}
                                    </div>
                                    {(!data.fraud_alerts?.recent || data.fraud_alerts.recent.length === 0) ? (
                                        <p className="text-xs text-text-secondary">No open fraud alerts.</p>
                                    ) : (
                                        <ul className="space-y-2 text-xs">
                                            {data.fraud_alerts.recent.map((a) => (
                                                <li key={a.id}
                                                    data-testid={`gov-fraud-alert-${a.id}`}
                                                    className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2">
                                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${SEVERITY_COLOR[a.severity || "low"]}`}>
                                                        {a.severity || "n/a"}
                                                    </span>
                                                    <span className="text-text-main">{a.type}</span>
                                                    <span className="ml-auto text-text-secondary tabular-nums">
                                                        {(a.confidence || 0).toFixed(2)}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </SectionShell>
                            </div>

                            <SectionShell
                                title="Auto-actions history"
                                subtitle="Reversible auto-actions from the fraud engine. Rollback available within 24h."
                                action={null}
                            >
                                {(!data.auto_actions?.recent || data.auto_actions.recent.length === 0) ? (
                                    <p className="text-xs text-text-secondary">No auto-actions recorded yet.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead className="text-[10px] uppercase tracking-widest text-text-secondary">
                                                <tr>
                                                    <th className="text-left py-2">Time</th>
                                                    <th className="text-left py-2">Action</th>
                                                    <th className="text-left py-2">Target</th>
                                                    <th className="text-left py-2">Status</th>
                                                    <th className="text-right py-2"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.auto_actions.recent.map((a) => (
                                                    <tr key={a.id}
                                                        data-testid={`gov-audit-${a.id}`}
                                                        className="border-t border-[var(--color-border)]/40">
                                                        <td className="py-2 text-text-secondary tabular-nums">
                                                            {(a.timestamp || "").slice(0, 19).replace("T", " ")}
                                                        </td>
                                                        <td className="py-2 text-text-main truncate max-w-[280px]">
                                                            <code className="text-[10px]">{a.action}</code>
                                                        </td>
                                                        <td className="py-2 text-text-secondary truncate max-w-[180px]">
                                                            {a.target_type || "—"} · <code className="text-[10px]">{(a.target_id || "").slice(0, 8)}</code>
                                                        </td>
                                                        <td className="py-2">
                                                            {a.reversed ? (
                                                                <span className="inline-flex items-center gap-1 text-[10px] text-[var(--color-success)]">
                                                                    <CheckCircle2 className="h-3 w-3" /> Reversed
                                                                </span>
                                                            ) : a.can_rollback ? (
                                                                <span className="inline-flex items-center gap-1 text-[10px] text-neutral-400">
                                                                    <Clock className="h-3 w-3" /> Reversible
                                                                </span>
                                                            ) : (
                                                                <span className="text-[10px] text-text-secondary">Window closed</span>
                                                            )}
                                                        </td>
                                                        <td className="py-2 text-right">
                                                            {a.can_rollback && (
                                                                <button
                                                                    disabled={rollingBack === a.id}
                                                                    onClick={() => rollback(a)}
                                                                    data-testid={`gov-rollback-${a.id}`}
                                                                    className="text-[10px] rounded-full border border-[var(--color-primary)]/40 text-[var(--color-primary)] px-3 py-1 hover:bg-[var(--color-primary)]/10 disabled:opacity-50"
                                                                >
                                                                    {rollingBack === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Rollback"}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </SectionShell>

                            <div className="grid gap-6 md:grid-cols-3 mt-6">
                                <SectionShell
                                    title="V50 Self-Optimization"
                                    subtitle="Weekly cron (Mon 09:00 UTC · opt-in) — meta-intelligence report"
                                    action={
                                        <a
                                            href="/admin/v50"
                                            data-testid="gov-open-v50"
                                            className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-1.5 font-medium flex items-center gap-1"
                                        >
                                            Open V50 <ArrowUpRight className="h-3 w-3" />
                                        </a>
                                    }
                                >
                                    <p className="text-xs text-text-secondary">
                                        Aggregates diagnostics, improvements, expansion, marketing,
                                        analytics, scaling and evolution into one strategic report.
                                    </p>
                                    <ul className="mt-3 space-y-1 text-[10px] text-text-secondary tabular-nums">
                                        {(data.crons?.v50_self_optimization || []).slice(0, 3).map((r) => (
                                            <li key={r.id} className="flex justify-between">
                                                <span>{(r.generated_at || "").slice(0, 19).replace("T", " ")}</span>
                                                <span>{r.totals?.suggestions_persisted ?? 0} suggestions · {r.status}</span>
                                            </li>
                                        ))}
                                        {(!data.crons?.v50_self_optimization || data.crons.v50_self_optimization.length === 0) && (
                                            <li className="italic">No run yet.</li>
                                        )}
                                    </ul>
                                </SectionShell>

                                <SectionShell
                                    title="Email Engine V10"
                                    subtitle="Weekly cron (Mon 09:00 UTC · opt-in) — email suggestions only, never sends"
                                    action={
                                        <a
                                            href="/admin/email-engine"
                                            data-testid="gov-open-email-engine"
                                            className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-1.5 font-medium flex items-center gap-1"
                                        >
                                            Open Email Engine <ArrowUpRight className="h-3 w-3" />
                                        </a>
                                    }
                                >
                                    <p className="text-xs text-text-secondary">
                                        Wraps Phase-7B drip, dynamic offers, ETF drafts and Fase-10 content
                                        into 6-engine email suggestions. Approve = acknowledged (no auto-send).
                                    </p>
                                    <ul className="mt-3 space-y-1 text-[10px] text-text-secondary tabular-nums">
                                        {(data.crons?.email_engine || []).slice(0, 3).map((r) => (
                                            <li key={r.id} className="flex justify-between">
                                                <span>{(r.generated_at || "").slice(0, 19).replace("T", " ")}</span>
                                                <span>{r.totals?.suggestions_persisted ?? 0} suggestions · {r.llm_calls_count || 0} llm</span>
                                            </li>
                                        ))}
                                        {(!data.crons?.email_engine || data.crons.email_engine.length === 0) && (
                                            <li className="italic">No run yet.</li>
                                        )}
                                    </ul>
                                </SectionShell>

                                <SectionShell
                                    title="SEO Engine V10"
                                    subtitle="Weekly cron (Mon 09:00 UTC · opt-in) — SEO suggestions only, never mutates content"
                                    action={
                                        <a
                                            href="/admin/seo-engine"
                                            data-testid="gov-open-seo-engine"
                                            className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-1.5 font-medium flex items-center gap-1"
                                        >
                                            Open SEO Engine <ArrowUpRight className="h-3 w-3" />
                                        </a>
                                    }
                                >
                                    <p className="text-xs text-text-secondary">
                                        Wraps Optimization, Recycling, Content Mesh and Scheduling
                                        into 9-block SEO suggestions (clusters, topics, internal/external
                                        linking, backlinks, variants, localizations, repurposings, scheduling).
                                    </p>
                                    <ul className="mt-3 space-y-1 text-[10px] text-text-secondary tabular-nums">
                                        {(data.crons?.seo_engine || []).slice(0, 3).map((r) => (
                                            <li key={r.id} className="flex justify-between">
                                                <span>{(r.generated_at || "").slice(0, 19).replace("T", " ")}</span>
                                                <span>{r.totals?.suggestions_persisted ?? 0} suggestions · {r.llm_calls_count || 0} llm</span>
                                            </li>
                                        ))}
                                        {(!data.crons?.seo_engine || data.crons.seo_engine.length === 0) && (
                                            <li className="italic">No run yet.</li>
                                        )}
                                    </ul>
                                </SectionShell>

                                <SectionShell
                                    title="Content Mesh"
                                    subtitle="Weekly cron · max 3 drafts"
                                    action={<RunNowButton label="Content Mesh"
                                        testid="gov-cmesh-run"
                                        onRun={async (dry) => {
                                            const r = await api.adminContentMeshRun(dry);
                                            await load();
                                            return r;
                                        }} />}
                                >
                                    <p className="text-xs text-text-secondary">
                                        Analyses top blog posts + user search queries to fill content gaps.
                                        All drafts land in the autonomy queue.
                                    </p>
                                    <ul className="mt-3 space-y-1 text-[10px] text-text-secondary tabular-nums">
                                        {(data.crons?.content_mesh || []).slice(0, 3).map((r) => (
                                            <li key={r.id} className="flex justify-between">
                                                <span>{(r.started_at || "").slice(0, 19).replace("T", " ")}</span>
                                                <span>{r.drafts_generated ?? 0} drafts{r.dry_run ? " · dry" : ""}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </SectionShell>

                                <SectionShell
                                    title="FAQ suggestions"
                                    subtitle="Monthly cron · max 5 Q&A"
                                    action={<RunNowButton label="FAQ suggestions"
                                        testid="gov-faq-run"
                                        onRun={async (dry) => {
                                            const r = await api.adminFaqRun(dry);
                                            await load();
                                            return r;
                                        }} />}
                                >
                                    <p className="text-xs text-text-secondary">
                                        Turns recurring searches + AI chat topics into admin-approvable FAQ pairs.
                                    </p>
                                    <ul className="mt-3 space-y-1 text-[10px] text-text-secondary tabular-nums">
                                        {(data.crons?.faq_suggestions || []).slice(0, 3).map((r) => (
                                            <li key={r.id} className="flex justify-between">
                                                <span>{(r.started_at || "").slice(0, 19).replace("T", " ")}</span>
                                                <span>{r.suggestions_generated ?? 0} pairs{r.dry_run ? " · dry" : ""}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </SectionShell>

                                <SectionShell
                                    title="ETF suggestions"
                                    subtitle="Weekly cron · max 2 bundles"
                                    action={<RunNowButton label="ETF suggestions"
                                        testid="gov-etf-run"
                                        onRun={async (dry) => {
                                            const r = await api.adminEtfRun(dry);
                                            await load();
                                            return r;
                                        }} />}
                                >
                                    <p className="text-xs text-text-secondary">
                                        Finds top co-purchased assets and drafts new bundles for admin review.
                                    </p>
                                    <ul className="mt-3 space-y-1 text-[10px] text-text-secondary tabular-nums">
                                        {(data.crons?.etf_suggestions || []).slice(0, 3).map((r) => (
                                            <li key={r.id} className="flex justify-between">
                                                <span>{(r.started_at || "").slice(0, 19).replace("T", " ")}</span>
                                                <span>{r.bundles_generated ?? 0} bundles{r.dry_run ? " · dry" : ""}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </SectionShell>
                            </div>

                            <SectionShell
                                title="Feature flags"
                                subtitle="Runtime toggles. Full editor lives in Admin → System settings."
                            >
                                <div className="grid gap-2 md:grid-cols-2">
                                    {(data.feature_flags || []).map((f) => (
                                        <div key={f.key}
                                            data-testid={`gov-flag-${f.key}`}
                                            className="flex items-center justify-between rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-3 py-2">
                                            <div className="min-w-0">
                                                <div className="text-xs text-text-main truncate"><code>{f.key}</code></div>
                                                <div className="text-[10px] text-text-secondary truncate">
                                                    {f.description}
                                                </div>
                                            </div>
                                            <span className={`ml-3 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border shrink-0 ${f.value ? "text-[var(--color-success)] border-[var(--color-success)]/40 bg-[var(--color-success)]/10" : "text-text-secondary border-[var(--color-border)]"}`}>
                                                {f.value ? "on" : "off"}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-3 text-right">
                                    <a href="/admin/system"
                                        data-testid="gov-open-system"
                                        className="text-xs text-[var(--color-secondary)] hover:text-[var(--color-primary)]">
                                        Manage flags in System →
                                    </a>
                                </div>
                            </SectionShell>
                        </>
                    )}
                </main>
            </div>
        </div>
    );
}
